/**
 * Responsive grid of filtered and sorted product cards.
 *
 * @module views/components/ProductListingResults
 * @memberof -Common
 */
import './ProductListingResults.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import constants from '@ulta/core/utils/constants/constants';

import ProductListingWrapper from '@ulta/components/ProductListingWrapper/ProductListingWrapper';

/**
 * Represents a ProductListingResults component
 *
 * @method
 * @param {ProductListingResultsProps} props - React properties passed from composition
 * @returns ProductListingResults
 */
export const ProductListingResults = React.forwardRef( ( props, _ )=>{
  const { componentKey } = props;


  return (
    <ProductListingWrapper { ...props }
      className='ProductListingResults'
      itemSelectorTemplate='[data-sku-id="{id}"] .pal-c-Link'
      itemSelectorClassName='ProductListingResults__productCard'
      itemSelectorAttribute='data-sku-id'
      itemIndexAttribute='skuId'
    >
      { ( { isFilterShown, onDataCaptureEvent, onIntersectionEvent, renderChildren } ) => (
        <ul
          className={ classNames( 'ProductListingResults__productList' ) }
          data-test='products-list'
        >
          { renderChildren( ( product, index )=>(
            <li
              className={ classNames( 'ProductListingResults__productCard', {
                'ProductListingResults__productCard--narrowed': isFilterShown
              } ) }
              data-test='products-list-item'
              key={ `${product.productName}_${index}` }
              data-sku-id={ product.skuId }
            >
              <AsyncComponent
                moduleName='ProductCard'
                componentKey={ componentKey }
                // TODO: this componentLastUpdated needs to be refactored -- https://ulta.atlassian.net/browse/DSOTF2-109266
                componentLastUpdated={ componentKey }
                onDataCaptureEvent={ onDataCaptureEvent }
                onIntersectionEvent={ onIntersectionEvent }
                skuId={ product.skuId }
                productId={ product.productId }
                action={ product.action }
                additionalOffersText={ product.additionalOffersText }
                addToBagAccessibility={ product.addToBagAccessibility }
                altImage={ product.altImage }
                badge={ product.badge }
                sponsoredBadgeLabel={ product.sponsoredBadgeLabel }
                bookmarkAccessibility={ product.bookmarkAccessibility }
                bookmarked={ product.bookmarked }
                brandName={ product.brandName }
                image={ product.image?.imageUrl }
                kitPrice={ product.kitPrice }
                listPrice={ product.listPrice }
                productName={ product.productName }
                promoText={ product.promoText }
                rating={ product.rating }
                reviewCount={ product.reviewCount }
                reviewAccessibilityLabel={ product.reviewAccessibilityLabel }
                salePrice={ product.salePrice }
                variantLabel={ product.variantLabel }
                hoverState={ false }
                displayBookmark={ !!product.bookmarked }
                addToBagAction={ product.addToBagAction }
                viewOptionAction={ product.viewOptionAction }
                removeFavoriteAction={ product.removeBookmarkAction }
                removeFavoriteDialog={ props.removeFavoriteDialog }
                salePriceAccessibilityLabel={ props.salePriceAccessibilityLabel }
                listPriceAccessibilityLabel={ props.listPriceAccessibilityLabel }
                messages={ props.messages }
              />
            </li>
          ) ) }
        </ul>
      ) }
    </ProductListingWrapper>
  );
} );

/**
 * Property type definitions
 *
 * @typedef ProductListingResultsProps
 * @type {object}
 * @property {array} items - Sets the items of the page
 * @property {boolean} loading - Flag to render the data or not
 * @property {object} filtersColumn - Sets the filters column for the page
 * @property {string} resultCountLabel - Sets the result count label for the page
 * @property {string} noResultsLabel - Sets the no result label for the page
 * @property {object} filtersResultAction - Sets the filtersResultAction for the page
 * @property {object} loadMoreAction - Sets the loadMoreAction for the page
 * @property {object} loadPreviousAction - Sets the loadPreviousAction for the page
 * @property {string} loadMoreUrl - Sets the load more client side url
 * @property {string} loadPreviousUrl - Sets the load previous client side url
 * @property {string} productsViewedLabel - Sets the productsViewedLabel for the page
 * @property {string} noFilteredResultsLabel - Sets the noFilteredResultsLabel for the page
 * @property {object} resetFiltersAction - Sets the resultFIltersAction for the page
 * @property {string} resetFiltersUrl - Sets the url for after a filter reset
 * @property {string} currentUrl - Sets the current url for page
 * @property {string} componentKey - Sets the component key
 * @property {object} removeFavoriteAction - Sets the remove Favorite Action
 * @property {object} removeFavoriteDialog - Sets the remove Favorite Dialog
 * @property {function} invokeMutation - Sets the invoke Mutation to send child component
 * @property {number} contentId - Sets the content id
 * @property {object} messages - Sets the error message
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
*/
export const propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  filtersColumn: PropTypes.object,
  resultCountLabel: PropTypes.string,
  noResultsLabel: PropTypes.string,
  filtersResultAction: PropTypes.object,
  loadMoreAction: PropTypes.object,
  loadPreviousAction: PropTypes.object,
  loadMoreUrl: PropTypes.string,
  loadPreviousUrl: PropTypes.string,
  productsViewedLabel: PropTypes.string,
  noFilteredResultsLabel: PropTypes.string,
  resetFiltersAction: PropTypes.object,
  resetFiltersUrl: PropTypes.string,
  currentUrl: PropTypes.string,
  componentKey: PropTypes.string,
  removeFavoriteAction: PropTypes.object,
  removeFavoriteDialog: PropTypes.object,
  invokeMutation: PropTypes.func,
  contentId: PropTypes.number,
  messages: PropTypes.object,
  salePriceAccessibilityLabel: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string
};
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ProductListingResults.propTypes = propTypes;
ProductListingResults.defaultProps = defaultProps;
ProductListingResults.displayName = 'ProductListingResults';

export default ProductListingResults;
